import { Driver } from '@automata/wfb-api';
import { ParameterDefinition } from '@automata/wfb-api/src/apis/wfbApiSchemas';
import set from 'lodash/set';
import size from 'lodash/size';

import * as TaskErrors from './taskErrors';
import { FieldValue } from '../../components/WorkcellFormModal/types';
import { ONE_OF_FIELDS } from '../../constants';
import { AllTaskProperties, PropertiesType } from '../../types';
import { validateField } from '../../utils';
import { WorkflowSerializationError } from '../shared/types';

export const getFieldErrorsForTasks = (
  taskIds: string[],
  driverForTaskByTaskId: Record<string, Driver>,
  taskPropertiesById: Record<string, AllTaskProperties>,
  parameterDefinitions: ParameterDefinition[]
): WorkflowSerializationError[] => {
  const errors: WorkflowSerializationError[] = [];

  for (const taskId of taskIds) {
    const oneOfFieldValues: Record<string, Record<string, FieldValue[]>> = {};
    const task = taskPropertiesById[taskId];
    if (task?.propertiesType && task.propertiesType !== PropertiesType.Action) {
      continue;
    }

    if (task === undefined) {
      errors.push({
        errorType: 'tasks',
        message: TaskErrors.NO_TASK_PROPERTIES,
        taskId,
      });
      continue;
    }

    const taskDriver = driverForTaskByTaskId[taskId]?.actions[task.taskType];

    if (taskDriver === undefined) {
      errors.push({
        errorType: 'tasks',
        message: TaskErrors.MISSING_INSTRUMENT,
        taskId,
      });
      continue;
    }

    Object.entries(taskDriver.arguments).forEach(([fieldName, fieldConfig]) => {
      // all validation happens in selectSlotConflictErrorsStrict selector
      if (fieldName === 'slot') return false;

      const parameterAppliedReferenceId =
        task.taskAppliedParameters?.[fieldName];
      const parameterReference = parameterDefinitions.find(
        (p) => p.id === parameterAppliedReferenceId
      );
      const fieldValue = parameterReference
        ? parameterReference.default
        : task.taskParameters?.[fieldName];
      const error = validateField(fieldValue as FieldValue, fieldConfig);

      // We need to use a selector that gets a collection of fields from realDrivers.json.
      // But so far there is no such information there.
      Object.entries(ONE_OF_FIELDS).forEach(([collectionName, fields]) => {
        // The field is part of one of the oneOff sets.
        if (fields.includes(fieldName) && fieldValue) {
          set(
            oneOfFieldValues,
            [collectionName, fieldName],
            fieldValue as FieldValue
          );
        }
      });

      if (!error) return;

      if (fieldConfig.type === 'int' && fieldConfig.units === 'seconds') {
        errors.push({
          errorType: 'tasks',
          message: TaskErrors.MISSING_TIME_ESTIMATE,
          taskId,
        });
      } else {
        errors.push({
          errorType: 'tasks',
          message: TaskErrors.INVALID_TASK_FIELD_VALUES,
          taskId,
        });
      }
    });

    Object.values(oneOfFieldValues).forEach((fieldValues) => {
      // We add only non-empty values to this object.
      // If there are more than 1 non-empty field from 1 collection, it is an error.
      if (size(fieldValues) > 1) {
        errors.push({
          errorType: 'tasks',
          message: TaskErrors.INVALID_TASK_FIELD_VALUES,
          taskId,
        });
      }
    });
  }

  return errors;
};
